import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';

// ── Icons ────────────────────────────────────────────────────────────────────
function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
function HashIcon({ className }) {
  return <Ico className={className}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></Ico>;
}
function RefreshIcon({ className }) {
  return <Ico className={className}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.67"/></Ico>;
}
function UserIcon({ className }) {
  return <Ico className={className}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Ico>;
}

export default function NumbersList({ theme = 'dark' }) {
  const { user }    = useContext(AuthContext);
  const [numbers, setNumbers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [error, setError]       = useState('');
  const [syncMsg, setSyncMsg]   = useState('');

  const isDark = theme === 'dark';

  // Theme tokens
  const text       = isDark ? 'text-white'            : 'text-slate-900';
  const textMuted  = isDark ? 'text-white/50'         : 'text-slate-500';
  const surface    = isDark ? 'bg-white/5'            : 'bg-slate-50';
  const theadBg    = isDark ? 'bg-white/5'            : 'bg-slate-50';
  const theadText  = isDark ? 'text-white/40'         : 'text-slate-500';
  const rowHover   = isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-slate-50';
  const divider    = isDark ? 'divide-white/8'        : 'divide-slate-100';
  const border     = isDark ? 'border-white/10'       : 'border-slate-200';
  const btnSync    = isDark
    ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/25'
    : 'bg-green-600 hover:bg-green-700 text-white border-transparent';
  const btnSecond  = isDark
    ? 'bg-white/8 hover:bg-white/12 text-white/60 border border-white/10'
    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-transparent';
  const phoneColor = isDark ? 'text-sky-400'          : 'text-blue-600';
  const btnAssign  = isDark
    ? 'text-sky-400 hover:text-sky-300 font-semibold text-sm transition'
    : 'text-blue-600 hover:text-blue-800 font-semibold text-sm transition';

  useEffect(() => { fetchNumbers(); }, []);

  const fetchNumbers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/numbers');
      setNumbers(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load numbers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMsg('');
      setError('');
      const response = await api.post('/numbers/sync');
      const { synced, added } = response.data;
      setSyncMsg(`Synced ${synced} number(s) from Telnyx${added > 0 ? ` — ${added} new added` : ' (no new numbers)'}.`);
      await fetchNumbers();
    } catch (err) {
      setError('Sync failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleAssign = async (numberId) => {
    try {
      await api.post('/numbers/assign', {
        numberIdOrPhoneNumber: numberId,
        userId: user.id,
      });
      fetchNumbers();
    } catch (err) {
      setError('Failed to assign number: ' + err.message);
    }
  };

  return (
    <div className={text}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold tracking-tight">Phone Numbers</h2>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={fetchNumbers}
          disabled={loading}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition disabled:opacity-40 border ${btnSecond}`}
        >
          <RefreshIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Refresh'}
        </motion.button>
      </div>

      {/* Error */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`border px-4 py-2.5 rounded-xl text-sm mb-4 ${
            isDark ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'bg-red-50 border-red-300 text-red-700'
          }`}
        >
          {error}
        </motion.div>
      )}

      {/* Sync success toast */}
      <AnimatePresence>
        {syncMsg && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className={`border px-4 py-2.5 rounded-xl text-sm mb-4 ${
              isDark ? 'bg-green-500/15 border-green-500/30 text-green-400' : 'bg-green-50 border-green-300 text-green-700'
            }`}
          >
            {syncMsg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading skeleton */}
      {loading && numbers.length === 0 && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`h-14 rounded-xl animate-pulse ${surface}`} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && numbers.length === 0 && !error && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center justify-center py-20 gap-4"
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${surface}`}>
            <HashIcon className={`h-7 w-7 ${textMuted}`} />
          </div>
          <div className="text-center">
            <p className={`font-medium ${text}`}>No phone numbers connected</p>
            <p className={`text-sm mt-1 ${textMuted}`}>Connect your Telnyx account in Settings, then sync</p>
          </div>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleSync}
            disabled={syncing}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition disabled:opacity-50 ${btnSync}`}
          >
            <RefreshIcon className="h-4 w-4" />
            {syncing ? 'Syncing…' : '↻ Sync from Telnyx'}
          </motion.button>
        </motion.div>
      )}

      {/* Numbers table */}
      {numbers.length > 0 && (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm min-w-[380px]">
            <thead>
              <tr className={theadBg}>
                <th className={`px-4 py-3 text-left font-medium text-xs uppercase tracking-wide ${theadText} rounded-l-xl`}>Number</th>
                <th className={`px-4 py-3 text-left font-medium text-xs uppercase tracking-wide ${theadText} hidden sm:table-cell`}>Status</th>
                <th className={`px-4 py-3 text-left font-medium text-xs uppercase tracking-wide ${theadText} hidden sm:table-cell`}>Assigned</th>
                <th className={`px-4 py-3 text-left font-medium text-xs uppercase tracking-wide ${theadText} rounded-r-xl`}>Action</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${divider}`}>
              {numbers.map((number, i) => (
                <motion.tr
                  key={number.id}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.3) }}
                  className={`transition ${rowHover}`}
                >
                  <td className={`px-4 py-3.5 font-mono font-medium ${phoneColor}`}>
                    {number.phoneNumber}
                  </td>
                  <td className="px-4 py-3.5 hidden sm:table-cell">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      number.status === 'available'
                        ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-800')
                        : (isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800')
                    }`}>
                      {number.status}
                    </span>
                  </td>
                  <td className={`px-4 py-3.5 hidden sm:table-cell ${textMuted}`}>
                    <div className="flex items-center gap-1.5 text-xs">
                      {number.assignedToUserId && <UserIcon className="h-3.5 w-3.5" />}
                      {number.assignedToUserId ? 'Assigned' : 'Unassigned'}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    {number.status === 'available' && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleAssign(number.id)}
                        className={btnAssign}
                      >
                        Assign
                      </motion.button>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer sync controls */}
      <div className={`mt-6 pt-6 border-t ${border} flex flex-wrap gap-3 items-center`}>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={handleSync}
          disabled={syncing}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition disabled:opacity-50 ${btnSync}`}
        >
          <RefreshIcon className="h-4 w-4" />
          {syncing ? 'Syncing…' : '↻ Sync from Telnyx'}
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={fetchNumbers}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition ${btnSecond}`}
        >
          <RefreshIcon className="h-3.5 w-3.5" />
          Refresh
        </motion.button>
      </div>
    </div>
  );
}
